/**
 * sharedTrip.ts — the pure logic behind /trip/shared/<token>.
 *
 * No React, no fetch (except the Wikipedia lookup at the bottom), so the page,
 * its metadata and the tests all read the same numbers:
 *
 *   stats      3 days · 3 stops · 621 km · 7h 40m driving (a loop's
 *              drive home isn't another stop)
 *   timeline   Day 1  Leave Melbourne 9:00 am → arrive Lorne about 11:35 am
 *   hero       the first night's campsite photo, else a free photo of the town
 *   title      "Great Ocean Road · 3 days · 621 km" for the share preview
 */

import type { TripLeg, TripStop } from '../../../types/trip';
import type { SharedTrip, SheetDrive, TripSheetData } from '../../../utils/tripApi';
import {
  buildSheetDays,
  driveSegments,
  formatDrive,
  formatKm,
  normaliseKind,
  stopNights,
  summariseSheet,
  type SheetDay,
} from '../../../utils/tripSheet';
import { samplePath } from './tripHighlights';
import { segmentIntoDays } from '../../../utils/tripPlanner';
import { dayColor } from '../../../utils/tripDayColors';
import { isLoop, placeCount } from '../../../utils/tripPlaces';

export { isLoop, placeCount };

export function toStops(trip: Pick<SharedTrip, 'stops'>): TripStop[] {
  return [...(trip.stops || [])]
    .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
    .map((s) => ({
      id: s.id,
      name: s.name || '',
      kind: (s.kind as TripStop['kind']) || 'camp',
      entityId: typeof s.entity_id === 'number' ? s.entity_id : undefined,
      lat: typeof s.lat === 'number' ? s.lat : undefined,
      lng: typeof s.lng === 'number' ? s.lng : undefined,
      notes: s.notes ?? undefined,
      tags: s.tags,
      nights: typeof s.nights === 'number' ? s.nights : undefined,
    }));
}

/* ── Clock ──────────────────────────────────────────────────────────────── */

/** Every drive day leaves at 9 am. A sensible start, and easy to read. */
export const DEPART_MINUTES = 9 * 60;
/** Australian fatigue advice: a 15 minute break every two hours. */
export const BREAK_EVERY_MIN = 120;
export const BREAK_MIN = 15;
/** Time at a stop you pass through (a look around, a coffee). */
export const VIA_STOP_MIN = 30;

/** Minutes after midnight → "9:00 am". Past midnight wraps to the next day. */
export function formatClock(minutes: number): string {
  if (!Number.isFinite(minutes)) return '';
  const m = ((Math.round(minutes) % 1440) + 1440) % 1440;
  const h24 = Math.floor(m / 60);
  const mm = String(m % 60).padStart(2, '0');
  const suffix = h24 < 12 ? 'am' : 'pm';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${mm} ${suffix}`;
}

/** Breaks owed on a drive of this length (exactly 2h needs none). */
export function breakMinutes(driveMinutes: number): number {
  if (!Number.isFinite(driveMinutes) || driveMinutes <= BREAK_EVERY_MIN) return 0;
  return Math.floor((driveMinutes - 1) / BREAK_EVERY_MIN) * BREAK_MIN;
}

/* ── Timed legs ─────────────────────────────────────────────────────────── */

export interface LegFigure {
  km?: number;
  minutes?: number;
  routed: boolean;
}

/**
 * `${fromId}>${toId}` → figures. Later sources win: straight-line estimates,
 * then the server's routed legs, then Google's road legs. Google wins because
 * it is what the planner shows after "Take this trip" (same call, same
 * rounding), so the two views can never disagree.
 */
export function legFigureMap(
  legs: TripLeg[],
  sheet?: Pick<TripSheetData, 'legs'> | null,
  roadLegs?: Record<string, { km: number; minutes: number }> | null
): Map<string, LegFigure> {
  const out = new Map<string, LegFigure>();
  legs.forEach((l) =>
    out.set(`${l.fromId}>${l.toId}`, { km: l.distanceKm, minutes: l.driveMinutes, routed: false })
  );
  (sheet?.legs || []).forEach((l) => {
    if (l.routed && typeof l.distance_km === 'number') {
      out.set(`${l.from_stop_id}>${l.to_stop_id}`, {
        km: l.distance_km,
        minutes: l.duration_minutes ?? undefined,
        routed: true,
      });
    }
  });
  Object.entries(roadLegs || {}).forEach(([key, v]) => {
    if (v.km > 0) out.set(key, { km: v.km, minutes: v.minutes, routed: true });
  });
  return out;
}

export interface TimedLeg {
  from: TripStop;
  to: TripStop;
  /** Minutes after midnight. */
  departAt: number;
  arriveAt: number;
  km?: number;
  minutes?: number;
  breakMin: number;
  routed: boolean;
}

/**
 * The legs of one drive day with a clock: leave at 9, a break every two
 * hours, half an hour at each stop on the way. Rest days have no legs.
 */
export function timedLegs(day: SheetDay, figures: Map<string, LegFigure>): TimedLeg[] {
  if (day.type !== 'drive') return [];
  const points = [day.from, ...day.vias, day.to];
  const out: TimedLeg[] = [];
  let t = DEPART_MINUTES;
  for (let i = 0; i < points.length - 1; i += 1) {
    const from = points[i];
    const to = points[i + 1];
    const f = figures.get(`${from.id}>${to.id}`);
    // A single-leg day uses the day's own (possibly routed) figures.
    const single = points.length === 2;
    const minutes = single && typeof day.driveMinutes === 'number' ? day.driveMinutes : f?.minutes;
    const km = single && typeof day.distanceKm === 'number' ? day.distanceKm : f?.km;
    const brk = breakMinutes(minutes ?? 0);
    const departAt = t;
    const arriveAt = t + (minutes ?? 0) + brk;
    out.push({
      from,
      to,
      departAt,
      arriveAt,
      km,
      minutes,
      breakMin: brk,
      routed: single ? day.routed : !!f?.routed,
    });
    t = arriveAt + (i < points.length - 2 ? VIA_STOP_MIN : 0);
  }
  return out;
}

/* ── Stats ──────────────────────────────────────────────────────────────── */

export interface TripStats {
  days: number;
  nights: number;
  /** Places visited: a loop's return home isn't counted again. */
  stops: number;
  km?: number;
  driveMinutes?: number;
  /** True when any figure is a straight-line estimate. */
  approx: boolean;
}

export function tripStats(stops: TripStop[], days: SheetDay[]): TripStats {
  const t = summariseSheet(days);
  return {
    days: t.days,
    nights: t.nights,
    stops: placeCount(stops),
    km: t.distanceKm,
    driveMinutes: t.driveMinutes || undefined,
    approx: !t.allRouted,
  };
}

/**
 * The trip's drive days as [from, to] stop indexes: the planner's own split
 * (segmentIntoDays, the same call as the planner's itinerary), so the shared
 * page's Day 2 is the planner's Day 2 after "Take this trip". Leg figures are
 * the best we have (road, then the server's routed legs, then estimates).
 */
export function planSegments(
  stops: TripStop[],
  legs: TripLeg[],
  driveHoursPerDay?: number | null
): Array<[number, number]> {
  const hours = typeof driveHoursPerDay === 'number' && driveHoursPerDay > 0 ? driveHoursPerDay : 6;
  return segmentIntoDays(stops, legs, hours * 60).map((d) => [d.firstStopIndex, d.lastStopIndex] as [number, number]);
}

/** The legs with the best figures we have for each (see legFigureMap). */
export function bestLegs(
  legs: TripLeg[],
  sheet?: Pick<TripSheetData, 'legs'> | null,
  roadLegs?: Record<string, { km: number; minutes: number }> | null
): TripLeg[] {
  const figures = legFigureMap(legs, sheet, roadLegs);
  return legs.map((l) => {
    const f = figures.get(`${l.fromId}>${l.toId}`);
    if (!f || !f.routed) return l;
    return { ...l, distanceKm: f.km, driveMinutes: f.minutes, source: 'road' as TripLeg['source'] };
  });
}

export function buildDays(
  stops: TripStop[],
  legs: TripLeg[],
  opts: {
    startDate?: string | null;
    sheet?: TripSheetData | null;
    roadLegs?: Record<string, { km: number; minutes: number }> | null;
    driveHoursPerDay?: number | null;
  }
): SheetDay[] {
  const best = bestLegs(legs, opts.sheet, opts.roadLegs);
  const segments = planSegments(stops, best, opts.driveHoursPerDay);
  return buildSheetDays(stops, {
    startDate: opts.startDate,
    legs: best,
    drives: mergeRoadDrives(stops, opts.sheet?.drives, opts.roadLegs, segments),
    driveHoursPerDay: opts.driveHoursPerDay ?? 6,
    segments,
  });
}

/** One day's colour (see dayColorMap). */
export function dayHexIn(days: Pick<SheetDay, 'dayNumber' | 'type'>[], dayNumber: number): string {
  return dayColorMap(days).get(dayNumber) ?? dayColor(0);
}

/**
 * Colour of a day on the shared page: the planner's tp.day-1..7 palette,
 * counted by DRIVING day (rest days take no colour), as the planner's list
 * and map count them.
 */
export function dayColorMap(days: Pick<SheetDay, 'dayNumber' | 'type'>[]): Map<number, string> {
  const out = new Map<number, string>();
  let drive = 0;
  days.forEach((d) => {
    if (d.type === 'drive') {
      out.set(d.dayNumber, dayColor(drive));
      drive += 1;
    } else {
      out.set(d.dayNumber, dayColor(Math.max(0, drive - 1)));
    }
  });
  return out;
}

/** "3 days" / "1 day". */
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Driving time for the stats strip: "7h 40m", or a dash when unknown. */
export function statHours(minutes?: number): string {
  if (typeof minutes !== 'number' || minutes <= 0) return '–';
  return formatDrive(minutes);
}

const NUMBER_WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve'];
const numberWord = (n: number) => NUMBER_WORDS[n] ?? String(n);
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function joinAnd(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * The one-line dek under the title, written from the trip itself:
 *   "Three days out of Melbourne: a night in Lorne, a night in Port Fairy, then home."
 *   "Four days from Sydney to Byron Bay, with two nights in Yamba."
 */
export function tripDek(stops: TripStop[], stats: Pick<TripStats, 'days'>): string {
  if (stops.length < 2) return '';
  const short = (s: TripStop) => s.name.split(',')[0].trim();
  const days = stats.days <= 1 ? 'A day' : `${cap(numberWord(stats.days))} days`;
  const nights: string[] = [];
  stops.slice(1, -1).forEach((s) => {
    const n = stopNights(s);
    if (n <= 0) return;
    nights.push(`${n === 1 ? 'a night' : `${numberWord(n)} nights`} in ${short(s)}`);
  });
  const start = short(stops[0]);
  if (isLoop(stops)) {
    if (!nights.length) return `${days} out of ${start} and back.`;
    return `${days} out of ${start}: ${nights.join(', ')}, then home.`;
  }
  const end = short(stops[stops.length - 1]);
  return nights.length ? `${days} from ${start} to ${end}, with ${joinAnd(nights)}.` : `${days} from ${start} to ${end}.`;
}

/** "Oct 2026" from an ISO date, or null. */
export function monthYear(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric', timeZone: 'Australia/Sydney' });
}

/**
 * "40+ people took this trip": exact under ten, then rounded down to the
 * ten (the hundred from 100, the thousand from 10,000) with a "+". Every
 * visitor reads the same number, even while the count ticks up between two
 * page loads, and it is never more than the truth.
 */
export function takeLine(takeCount?: number | null): string | null {
  if (typeof takeCount !== 'number' || !Number.isFinite(takeCount) || takeCount <= 0) return null;
  const n = Math.floor(takeCount);
  if (n === 1) return '1 person took this trip';
  if (n < 10) return `${n} people took this trip`;
  const step = n < 100 ? 10 : n < 10000 ? 100 : 1000;
  const floor = Math.floor(n / step) * step;
  // Always "40+" from ten up (40 is "40 or more" too), so a take between two
  // page loads, or between a laptop and a phone, never changes the words.
  return `${floor.toLocaleString('en-AU')}+ people took this trip`;
}

/**
 * The byline's facts, only the true ones:
 *   ["Shared Oct 2026", "38 people took this trip"]
 */
export function bylineBits({ sharedAt, takeCount }: { sharedAt?: string | null; takeCount?: number | null }): string[] {
  const out: string[] = [];
  const when = monthYear(sharedAt);
  if (when) out.push(`Shared ${when}`);
  const takes = takeLine(takeCount);
  if (takes) out.push(takes);
  return out;
}

/**
 * The trip's own night count for each day that ends in a bed: Day 2's night
 * in Port Fairy is "Night 2" of the trip, whatever the stop's own count.
 */
export function tripNightNumbers(days: Pick<SheetDay, 'dayNumber' | 'night'>[]): Map<number, number> {
  const out = new Map<number, number>();
  let n = 0;
  days.forEach((d) => {
    if (!d.night) return;
    n += 1;
    out.set(d.dayNumber, n);
  });
  return out;
}

/** "Night 2", or "Night 3 · 2nd of 2 here" for a longer stay. */
export function nightLabel(tripNight: number, night: { nightNumber: number; ofNights: number }): string {
  if (night.ofNights <= 1) return `Night ${tripNight}`;
  return `Night ${tripNight} · ${night.nightNumber} of ${night.ofNights} here`;
}

type Pt = { lat: number; lng: number };

/**
 * One drive day's road, best first: the server's line for the day, Google's
 * legs joined up, else straight lines between the day's stops.
 */
export function dayPath(
  day: Pick<SheetDay, 'type' | 'from' | 'to' | 'vias'>,
  drivePaths: Record<string, Pt[]>,
  legPaths?: Record<string, Pt[]> | null
): { path: Pt[]; routed: boolean } {
  if (day.type !== 'drive') return { path: [], routed: false };
  const points = [day.from, ...day.vias, day.to].filter((s) => typeof s.lat === 'number' && typeof s.lng === 'number');
  const server = drivePaths[`${day.from.id}>${day.to.id}`];
  if (server && server.length >= 2) return { path: server, routed: true };
  if (legPaths && points.length >= 2) {
    const parts = points.slice(0, -1).map((p, i) => legPaths[`${p.id}>${points[i + 1].id}`]);
    if (parts.every((x) => x && x.length >= 2)) return { path: parts.flat() as Pt[], routed: true };
  }
  return { path: points.map((p) => ({ lat: p.lat as number, lng: p.lng as number })), routed: false };
}

export function routeLine(stops: TripStop[]): string {
  const names = stops.map((s) => s.name.split(',')[0].trim()).filter(Boolean);
  // Squash immediate repeats (a camp listed twice in a row).
  return names.filter((n, i) => i === 0 || n !== names[i - 1]).join(' → ');
}

/**
 * The hero's kicker, short enough for one line on a phone. A loop names its
 * home once ("Loop from Melbourne · Lorne · Port Fairy"), so the places listed
 * are the stops counted in the stats strip.
 */
/** The eyebrow for a narrow screen: "Loop · 3 days" (the dek names the stops). */
export function shortEyebrow(stops: TripStop[], days: number): string {
  const kind = stops.length >= 3 && isLoop(stops) ? 'Loop' : 'Road trip';
  return days > 0 ? `${kind} · ${plural(days, 'day')}` : kind;
}

export function routeEyebrow(stops: TripStop[]): string {
  if (stops.length >= 3 && isLoop(stops)) {
    const names = stops.slice(0, -1).map((s) => s.name.split(',')[0].trim()).filter(Boolean);
    const uniq = names.filter((n, i) => i === 0 || n !== names[i - 1]);
    return `Loop from ${uniq[0]}${uniq.length > 1 ? ` · ${uniq.slice(1).join(' · ')}` : ''}`;
  }
  return routeLine(stops);
}

/**
 * The lede under the hero: the trip told day by day in plain sentences, from
 * the same figures as the day cards ("Day 1 runs 143 km to Lorne.").
 */
export function tripLede(
  days: Pick<SheetDay, 'dayNumber' | 'type' | 'to' | 'distanceKm'>[],
  opts: { loop?: boolean } = {}
): string {
  const drives = days.filter((d) => d.type === 'drive' && typeof d.distanceKm === 'number');
  const longest = drives.length > 2 ? drives.reduce((a, b) => ((b.distanceKm ?? 0) > (a.distanceKm ?? 0) ? b : a)) : null;
  const last = days[days.length - 1];
  const out: string[] = [];
  days.forEach((d, i) => {
    const to = d.to.name.split(',')[0].trim();
    if (d.type !== 'drive') {
      out.push(`Day ${d.dayNumber} is a rest day in ${to}.`);
      return;
    }
    const km = typeof d.distanceKm === 'number' ? formatKm(d.distanceKm) : null;
    const isLast = d === last;
    if (isLast && opts.loop) {
      out.push(d === longest && km ? `Day ${d.dayNumber} is the long one home to ${to}: ${km}.` : `Day ${d.dayNumber} takes you home to ${to}${km ? `, ${km}` : ''}.`);
    } else if (isLast) {
      out.push(`Day ${d.dayNumber} finishes in ${to}${km ? `, ${km} on` : ''}.`);
    } else if (i === 0) {
      out.push(km ? `Day ${d.dayNumber} runs ${km} to ${to}.` : `Day ${d.dayNumber} heads to ${to}.`);
    } else {
      out.push(km ? `Day ${d.dayNumber} is ${km} on to ${to}.` : `Day ${d.dayNumber} carries on to ${to}.`);
    }
  });
  return out.join(' ');
}

/** The trip's first stop note, for the pull quote: { text, place }. */
export function pullQuote(stops: TripStop[]): { text: string; place: string } | null {
  for (const s of stops) {
    const text = (s.notes || '').trim();
    if (text.length >= 12 && text.length <= 160) return { text, place: s.name.split(',')[0].trim() };
  }
  return null;
}

/* ── Share preview ──────────────────────────────────────────────────────── */

export function shareTitle(name: string | null | undefined, stats: TripStats): string {
  const title = (name || '').trim() || 'Road trip';
  const bits = [title, plural(stats.days, 'day')];
  if (typeof stats.km === 'number' && stats.km > 0) {
    bits.push(`${stats.approx ? '~' : ''}${formatKm(stats.km)}`);
  }
  return bits.join(' · ');
}

export function shareDescription(stops: TripStop[], stats: TripStats): string {
  const parts = [`${routeLine(stops)}.`];
  const facts: string[] = [];
  if (stats.nights > 0) facts.push(plural(stats.nights, 'night'));
  if (stats.driveMinutes) facts.push(`${formatDrive(stats.driveMinutes)} on the road`);
  if (facts.length) parts.push(`${facts.join(', ')}.`);
  parts.push('Take this trip and make it yours.');
  return parts.join(' ');
}

/* ── Hero ───────────────────────────────────────────────────────────────── */

/** Where the trip sleeps first. Falls back to the far end of the trip. */
export function heroStop(stops: TripStop[]): TripStop | null {
  if (!stops.length) return null;
  const firstNight = stops.find(
    (s, i) => i > 0 && stopNights(s) > 0 && normaliseKind(s.kind) !== 'end'
  );
  if (firstNight) return firstNight;
  // No nights: the destination, or (for a loop) the stop furthest along.
  return isLoop(stops) ? stops[Math.floor(stops.length / 2)] : stops[stops.length - 1];
}

export interface HeroPick {
  /** 'camp' = a listing photo we can use straight away; 'place' = look the town up. */
  kind: 'camp' | 'place';
  stop: TripStop;
  url?: string;
  caption: string;
}

const usableUrl = (u?: string | null): u is string => typeof u === 'string' && /^https?:\/\/\S+$/.test(u);

/**
 * The night's own campsite photo wins. Then any campsite on the trip with a
 * photo. Otherwise the caller looks up a free photo of the first night's town.
 */
export function pickHero(stops: TripStop[], entities: TripSheetData['entities'] = {}): HeroPick | null {
  const first = heroStop(stops);
  if (!first) return null;
  const imageFor = (s: TripStop) =>
    typeof s.entityId === 'number' ? entities[String(s.entityId)]?.image : undefined;
  const own = imageFor(first);
  if (usableUrl(own)) {
    return { kind: 'camp', stop: first, url: own, caption: entities[String(first.entityId)]?.name || first.name };
  }
  const nights = stops.filter((s) => stopNights(s) > 0);
  const other = [...nights, ...stops].find((s) => usableUrl(imageFor(s)));
  if (other) {
    return { kind: 'camp', stop: other, url: imageFor(other) as string, caption: entities[String(other.entityId)]?.name || other.name };
  }
  return { kind: 'place', stop: first, caption: first.name.split(',')[0].trim() };
}

const STATE_NAMES: Record<string, string> = {
  VIC: 'Victoria',
  NSW: 'New South Wales',
  QLD: 'Queensland',
  SA: 'South Australia',
  WA: 'Western Australia',
  TAS: 'Tasmania',
  NT: 'Northern Territory',
  ACT: 'Australian Capital Territory',
};

/** Wikipedia titles to try for a town: "Lorne, VIC" → ["Lorne, Victoria", "Lorne"]. */
export function wikiTitles(name: string): string[] {
  const raw = (name || '').trim();
  if (!raw || /^-?\d/.test(raw) || /dropped pin|my location/i.test(raw)) return [];
  const parts = raw.split(',').map((p) => p.trim()).filter(Boolean);
  const base = parts[0];
  if (!base || base.length < 3) return [];
  const state = parts
    .slice(1)
    .map((p) => p.replace(/\s+\d{4}$/, '').toUpperCase())
    .find((p) => STATE_NAMES[p]);
  return state ? [`${base}, ${STATE_NAMES[state]}`, base] : [base];
}

interface WikiPage {
  title?: string;
  index?: number;
  thumbnail?: { source?: string; width?: number };
}

/**
 * The best page from a geosearch: the one named like the stop (the town's own
 * article), else the nearest with a decent photo.
 */
export function pickWikiPage(pages: WikiPage[], name: string, minWidth = 600): string | null {
  const base = (name || '').split(',')[0].trim().toLowerCase();
  const withPhoto = pages
    .filter((p) => p.thumbnail?.source && (p.thumbnail.width ?? minWidth) >= minWidth)
    .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
  const named = base ? withPhoto.find((p) => (p.title || '').toLowerCase().startsWith(base)) : undefined;
  return (named || withPhoto[0])?.thumbnail?.source ?? null;
}

async function wikiQuery(params: string, timeoutMs: number): Promise<WikiPage[]> {
  const url = `https://en.wikipedia.org/w/api.php?action=query&format=json&origin=*&redirects=1&${params}`;
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;
  try {
    const res = await fetch(url, { signal: ctrl?.signal, headers: { Accept: 'application/json' } });
    if (!res.ok) return [];
    const data = await res.json();
    return data?.query?.pages ? (Object.values(data.query.pages) as WikiPage[]) : [];
  } catch {
    return [];
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * A big, FREE-LICENSED lead photo of a place from Wikipedia (Wikimedia
 * Commons only: pilicense=free). Articles near the stop's coordinates first
 * (so "Lorne" finds the Victorian town, not the Scottish one), then by name.
 * Null when there isn't one.
 */
export async function fetchPlaceHero(
  stop: Pick<TripStop, 'name' | 'lat' | 'lng'>,
  { width = 1600, timeoutMs = 4000 }: { width?: number; timeoutMs?: number } = {}
): Promise<string | null> {
  // A hero needs a real photo, not a logo; a thumbnail only needs to fill its box.
  const minWidth = Math.min(600, width);
  const photo = `prop=pageimages&piprop=thumbnail&pithumbsize=${width}&pilicense=free`;
  if (typeof stop.lat === 'number' && typeof stop.lng === 'number') {
    const pages = await wikiQuery(
      `generator=geosearch&ggscoord=${stop.lat}|${stop.lng}&ggsradius=10000&ggslimit=12&${photo}`,
      timeoutMs
    );
    const hit = pickWikiPage(pages, stop.name, minWidth);
    if (hit) return hit;
  }
  for (const title of wikiTitles(stop.name)) {
    const hit = pickWikiPage(await wikiQuery(`${photo}&titles=${encodeURIComponent(title)}`, timeoutMs), stop.name, minWidth);
    if (hit) return hit;
  }
  return null;
}

/* ── Road figures from the map ──────────────────────────────────────────── */

/**
 * Per-day drives with the best figures we have. When Google gave road
 * figures for every leg of a day, those set the km and minutes (the planner
 * reads the same ones), and the server's drive still brings its fuel stops,
 * warnings and road line. Otherwise a routed server drive stands. Days with
 * neither are left out, so the sheet falls back to estimates.
 */
export function mergeRoadDrives(
  stops: TripStop[],
  sheetDrives: SheetDrive[] | null | undefined,
  roadLegs: Record<string, { km: number; minutes: number }> | null | undefined,
  segments?: Array<[number, number]>
): SheetDrive[] {
  const byPair = new Map<string, SheetDrive>();
  (sheetDrives || []).forEach((d) => byPair.set(`${d.from_stop_id}>${d.to_stop_id}`, d));
  const out: SheetDrive[] = [];
  for (const [a, b] of segments ?? driveSegments(stops)) {
    const key = `${stops[a].id}>${stops[b].id}`;
    const server = byPair.get(key);
    const parts = [];
    for (let i = a; i < b; i += 1) parts.push(roadLegs?.[`${stops[i].id}>${stops[i + 1].id}`]);
    if (parts.length && parts.every((p) => p && p.km > 0)) {
      out.push({
        ...(server || {}),
        from_stop_id: stops[a].id,
        to_stop_id: stops[b].id,
        distance_km: Number(parts.reduce((s, p) => s + (p as { km: number }).km, 0).toFixed(1)),
        duration_minutes: parts.reduce((s, p) => s + (p as { minutes: number }).minutes, 0),
        routed: true,
        warnings: server?.warnings || [],
        fuel_stops: server?.fuel_stops || [],
        fuel_gap: server?.fuel_gap ?? null,
      });
    } else if (server) {
      out.push(server);
    }
  }
  return out;
}

/** `${fromId}>${toId}` → the day's routed line from the server sheet. */
export function drivePathMap(sheetDrives: SheetDrive[] | null | undefined): Record<string, { lat: number; lng: number }[]> {
  const out: Record<string, { lat: number; lng: number }[]> = {};
  (sheetDrives || []).forEach((d) => {
    const pts = (d.polyline || [])
      .filter((p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]))
      .map((p) => ({ lat: p[0], lng: p[1] }));
    if (d.routed && pts.length >= 2) out[`${d.from_stop_id}>${d.to_stop_id}`] = pts;
  });
  return out;
}

/**
 * Where to look for highlights: points along each drive day's road (rest
 * days get none), skipping the trip's home end(s) so a city's stadiums and
 * museums don't crowd out the coast.
 */
export function highlightSamples(
  stops: TripStop[],
  days: SheetDay[],
  drivePaths: Record<string, Pt[]>,
  legPaths?: Record<string, Pt[]> | null,
  { everyKm = 30, perDay = 6 }: { everyKm?: number; perDay?: number } = {}
): Pt[][] {
  const ends = [stops[0], stops[stops.length - 1]]
    .filter((s) => s && typeof s.lat === 'number' && typeof s.lng === 'number')
    .map((s) => ({ lat: s.lat as number, lng: s.lng as number }));
  return days.map((d) => {
    if (d.type !== 'drive') return [];
    const { path } = dayPath(d, drivePaths, legPaths);
    // A straight line has two points; fill it in so sampling has something to step along.
    const dense = path.length === 2 ? densify(path[0], path[1], 24) : path;
    return samplePath(dense, { everyKm, max: perDay, avoid: ends, avoidKm: 20 });
  });
}

function densify(a: Pt, b: Pt, n: number): Pt[] {
  return Array.from({ length: n + 1 }, (_, i) => ({ lat: a.lat + ((b.lat - a.lat) * i) / n, lng: a.lng + ((b.lng - a.lng) * i) / n }));
}

/**
 * How tall a map of this width must be to frame the route snugly (Web
 * Mercator), clamped. A long, low route (the Great Ocean Road) gets a short,
 * wide map instead of one that is mostly sea and paddock.
 */
export function fitMapHeight(
  points: Pt[],
  width: number,
  { pad = 48, extraTop = 8, min = 360, max = 900 }: { pad?: number; extraTop?: number; min?: number; max?: number } = {}
): number {
  if (points.length < 2 || width <= 2 * pad) return Math.max(min, Math.min(max, Math.round(width * 0.75)));
  const merc = (lat: number) => Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));
  const lats = points.map((p) => p.lat);
  const lngs = points.map((p) => p.lng);
  const dy = merc(Math.max(...lats)) - merc(Math.min(...lats));
  const dx = ((Math.max(...lngs) - Math.min(...lngs)) * Math.PI) / 180;
  if (dx <= 0) return max;
  const inner = (width - 2 * pad) * (dy / dx);
  return Math.round(Math.max(min, Math.min(max, inner + 2 * pad + extraTop)));
}

/* ── Static map (first paint) ───────────────────────────────────────────── */

export interface StaticView {
  lng: number;
  lat: number;
  /** Web Mercator zoom, 256px tiles (the same scale as Google's and the tile server's). */
  zoom: number;
  width: number;
  height: number;
  /** lat/lng → pixel in the width × height image. */
  project: (p: Pt) => { x: number; y: number };
}

const mercY = (lat: number) => {
  const s = Math.sin((Math.max(-85, Math.min(85, lat)) * Math.PI) / 180);
  return 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI);
};
const mercX = (lng: number) => lng / 360 + 0.5;

/**
 * The centre and zoom that frame these points in a width × height picture,
 * with `pad` pixels clear on every side (plus `extraTop` for the map key).
 * Null without at least one point.
 */
export function staticView(
  points: Pt[],
  width: number,
  height: number,
  { pad = 48, extraTop = 0, maxZoom = 12 }: { pad?: number; extraTop?: number; maxZoom?: number } = {}
): StaticView | null {
  const pts = points.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  if (!pts.length || width <= 0 || height <= 0) return null;
  const xs = pts.map((p) => mercX(p.lng));
  const ys = pts.map((p) => mercY(p.lat));
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const innerW = Math.max(1, width - 2 * pad);
  const innerH = Math.max(1, height - 2 * pad - extraTop);
  const spanX = Math.max(maxX - minX, 1e-9);
  const spanY = Math.max(maxY - minY, 1e-9);
  // Rounded first: the picture is rendered at exactly this zoom.
  const zoom = Math.round(Math.min(maxZoom, Math.log2(Math.min(innerW / (256 * spanX), innerH / (256 * spanY)))) * 100) / 100;
  const world = 256 * Math.pow(2, zoom);
  // Centre of the box, nudged down by half the extra top room.
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2 - extraTop / 2 / world;
  const lng = (cx - 0.5) * 360;
  const lat = (Math.atan(Math.sinh(Math.PI * (1 - 2 * cy))) * 180) / Math.PI;
  return {
    lng,
    lat,
    zoom,
    width,
    height,
    project: (p) => ({ x: width / 2 + (mercX(p.lng) - cx) * world, y: height / 2 + (mercY(p.lat) - cy) * world }),
  };
}

/** The same view again from its numbers (it travels to the planner as JSON). */
export function viewFrom(v: { lng: number; lat: number; zoom: number; width: number; height: number }): StaticView | null {
  if (![v.lng, v.lat, v.zoom, v.width, v.height].every((n) => typeof n === 'number' && Number.isFinite(n)) || v.width <= 0 || v.height <= 0) return null;
  const world = 256 * Math.pow(2, v.zoom);
  const cx = mercX(v.lng);
  const cy = mercY(v.lat);
  return {
    lng: v.lng,
    lat: v.lat,
    zoom: v.zoom,
    width: v.width,
    height: v.height,
    project: (p) => ({ x: v.width / 2 + (mercX(p.lng) - cx) * world, y: v.height / 2 + (mercY(p.lat) - cy) * world }),
  };
}

/** Our own tile server's static render of that view (2x for crisp screens). */
export function staticMapUrl(view: Pick<StaticView, 'lng' | 'lat' | 'zoom' | 'width' | 'height'>, base = 'https://tiles.trip-planner.com.au'): string {
  return `${base}/styles/basic/static/${view.lng.toFixed(4)},${view.lat.toFixed(4)},${view.zoom}/${Math.round(view.width)}x${Math.round(view.height)}@2x.png`;
}

/** A path as SVG "M x y L …", thinned to points at least `minGap` px apart. */
export function svgPath(points: { x: number; y: number }[], minGap = 1.5): string {
  if (!points.length) return '';
  const out: { x: number; y: number }[] = [points[0]];
  for (let i = 1; i < points.length; i += 1) {
    const prev = out[out.length - 1];
    const p = points[i];
    if (i === points.length - 1 || Math.hypot(p.x - prev.x, p.y - prev.y) >= minGap) out.push(p);
  }
  return out.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
}
